import { useState, useEffect } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import {
  Card,
  CardContent,
  Button,
  Badge,
  Input,
} from '@/components/ui'
import { Search, Eye, Download, FileText, Clock, User, Loader2 } from 'lucide-react'
import { jobsApi } from '@/services/api'

interface HistoryItem {
  id: string
  customerId: string
  type: string
  createdAt: string
  status: string
  riskScore: number | null
  memoId: string | null
}

const typeColors: Record<string, string> = {
  amazon: 'bg-dashboard-orange',
  brcs: 'bg-dashboard-teal',
  brig: 'bg-dashboard-purple',
  full: 'bg-dashboard-navy',
}

export function History() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const customerFilter = searchParams.get('customer')
  
  const [items, setItems] = useState<HistoryItem[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const params: any = { pageSize: 50 }
        if (customerFilter) {
          params.customerId = customerFilter
        }
        const { jobs } = await jobsApi.list(params)
        
        const historyItems: HistoryItem[] = jobs.map((job: any) => ({
          id: job.id,
          customerId: job.customerId,
          type: job.type,
          createdAt: job.createdAt,
          status: job.status,
          riskScore: job.result?.risk_score || null,
          memoId: job.output_memo_id || null,
        }))
        
        setItems(historyItems)
      } catch (error) {
        console.error('Failed to fetch history:', error)
      } finally {
        setLoading(false)
      }
    }
    
    fetchHistory()
  }, [customerFilter])

  const handleExport = (item: HistoryItem) => {
    if (item.memoId) {
      // Navigate to memo viewer with export flag
      navigate(`/memo/${encodeURIComponent(item.memoId)}?export=true`)
    } else {
      console.warn('No memo available for this job')
    }
  }

  const filteredItems = items.filter(item => 
    item.customerId.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.type.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge variant="success">Completed</Badge>
      case 'running':
        return <Badge variant="warning">Running</Badge>
      case 'pending':
        return <Badge variant="secondary">Pending</Badge>
      case 'failed':
        return <Badge variant="destructive">Failed</Badge>
      default:
        return <Badge>{status}</Badge>
    }
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analysis History</h1>
          <p className="text-muted-foreground">
            {customerFilter 
              ? `Viewing history for ${customerFilter}`
              : 'View and manage past credit analyses'
            }
          </p>
        </div>
        {customerFilter && (
          <Link to="/history">
            <Button variant="outline">View All History</Button>
          </Link>
        )}
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by customer ID or type..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* History List */}
      <div className="space-y-4">
        {loading ? (
          <Card>
            <CardContent className="py-16 flex justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </CardContent>
          </Card>
        ) : filteredItems.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">No Analysis History</h3>
              <p className="text-muted-foreground mb-4">
                {searchTerm 
                  ? 'No results match your search.'
                  : "You haven't run any credit analyses yet. Upload customer data to get started."
                }
              </p>
              {!searchTerm && (
                <Link to="/upload">
                  <Button className="gap-2">
                    Start New Analysis
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        ) : (
          filteredItems.map((item) => (
            <Card key={item.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  {/* Icon */}
                  <div className={`p-3 rounded-lg ${typeColors[item.type] || 'bg-gray-500'}`}>
                    <FileText className="h-6 w-6 text-white" />
                  </div>

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900">
                        {item.customerId}
                      </h3>
                      <Badge variant="outline" className="uppercase text-[10px]">
                        {item.type}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {item.customerId}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDate(item.createdAt)}
                      </span>
                    </div>
                  </div>

                  {/* Score & Status */}
                  <div className="text-center px-4">
                    {item.riskScore !== null ? (
                      <>
                        <div className="text-2xl font-bold text-dashboard-navy">
                          {Math.round(item.riskScore)}/10
                        </div>
                        <Badge variant={item.riskScore <= 3 ? 'success' : item.riskScore <= 6 ? 'warning' : 'destructive'}>
                          {item.riskScore <= 3 ? 'Low Risk' : item.riskScore <= 6 ? 'Medium Risk' : 'High Risk'}
                        </Badge>
                      </>
                    ) : (
                      getStatusBadge(item.status)
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Link to={`/analysis/${item.id}`}>
                      <Button variant="outline" size="sm" className="gap-2">
                        <Eye className="h-4 w-4" />
                        View
                      </Button>
                    </Link>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="gap-2"
                      onClick={() => handleExport(item)}
                      disabled={!item.memoId}
                    >
                      <Download className="h-4 w-4" />
                      Export
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  )
}
